import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import DesignTokens from '../../styles/DesignTokens';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import { getDestinationTheme } from '../../data/destinationThemes';
import { imagesApi } from '../../services/api';
import ThemedParticles from './ThemedParticles';

/* ─── 3 horizontal steps mapped to backend states ─── */

const STEPS = [
  {
    id: 'curating',
    label: 'Curating Attractions',
    icon: 'map-pin',
    states: ['QUEUED', 'SKELETON_CREATED', 'PREFERENCES_PARSED', 'GRAPH_DATA_READY'],
  },
  {
    id: 'optimizing',
    label: 'Optimizing Route',
    icon: 'route',
    states: ['ROUTE_OPTIMIZED', 'LLM_GENERATION_STARTED'],
  },
  {
    id: 'budget',
    label: 'Calculating Budget',
    icon: 'calculator',
    states: ['LLM_GENERATION_COMPLETED', 'POST_PROCESSING'],
  },
];

const STATE_ORDER = [
  'QUEUED', 'SKELETON_CREATED', 'PREFERENCES_PARSED', 'GRAPH_DATA_READY',
  'ROUTE_OPTIMIZED', 'LLM_GENERATION_STARTED',
  'LLM_GENERATION_COMPLETED', 'POST_PROCESSING', 'COMPLETED'
];

const getActiveStepIndex = (state) => {
  if (!state) return 0;
  if (state === 'COMPLETED') return STEPS.length;
  for (let i = STEPS.length - 1; i >= 0; i--) {
    if (STEPS[i].states.includes(state)) return i;
  }
  const stateIdx = STATE_ORDER.indexOf(state);
  if (stateIdx <= 3) return 0;
  if (stateIdx <= 5) return 1;
  return 2;
};

/* ─── Micro-Question data ─── */

const MICRO_QUESTIONS = [
  { question: 'What do you prefer?', optionA: '💎 Hidden gems', optionB: '🏛️ Iconic landmarks' },
  { question: 'Your travel pace?', optionA: '⚡ Pack it all in', optionB: '🐢 Slow & relaxed' },
  { question: 'Food style?', optionA: '🍜 Street food', optionB: '🍽️ Fine dining' },
];

/* ─── Animations ─── */

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ─── Styled Components ─── */

const Container = styled(motion.div)`
  padding: ${DesignTokens.spacing.xl};
  max-width: 520px;
  margin: 0 auto;
  width: 100%;
`;

const Heading = styled(motion.h2)`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  text-align: center;
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
  line-height: ${DesignTokens.typography.lineHeight.heading};
`;

const Subheading = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin: 0 0 ${DesignTokens.spacing.xl} 0;
`;

/* ─── Horizontal Steps ─── */

const StepsRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  margin-bottom: ${DesignTokens.spacing.xl};
  padding: 0 ${DesignTokens.spacing.sm};
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 140px;
  position: relative;
`;

const StepConnector = styled.div`
  height: 2px;
  flex: 0 0 32px;
  margin-top: 24px;
  background: ${props => props.$completed
    ? 'rgba(66, 133, 244, 0.6)'
    : 'rgba(255, 255, 255, 0.1)'};
  transition: background 0.5s ease;
`;

const StepCircle = styled(motion.div)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  transition: all 0.5s ease;
  margin-bottom: 10px;

  ${props => props.$status === 'completed' && `
    background: #22C55E;
    color: white;
    box-shadow: 0 0 12px rgba(34, 197, 94, 0.4);
  `}
  ${props => props.$status === 'active' && css`
    background: linear-gradient(135deg, #4285F4, #1A73E8);
    color: white;
    box-shadow: 0 0 20px rgba(66, 133, 244, 0.5);
    animation: ${pulse} 1.8s ease-in-out infinite;
  `}
  ${props => props.$status === 'pending' && `
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.1);
  `}
`;

const StepLabel = styled.span`
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.35)'};
  text-align: center;
  line-height: 1.3;
  transition: all 0.3s ease;
`;

/* ─── Progress bar ─── */

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: ${DesignTokens.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${DesignTokens.spacing.sm};
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${GlassmorphismTokens.gradient.progress};
  border-radius: ${DesignTokens.borderRadius.full};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    background-size: 200% 100%;
    ${css`animation: ${shimmer} 1.5s infinite;`}
  }
`;

const ProgressLabel = styled.div`
  text-align: center;
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.5);
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  margin-bottom: ${DesignTokens.spacing.lg};
`;

/* ─── Photo Carousel ─── */

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${DesignTokens.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const CarouselImage = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
`;

const CarouselDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.4)'};
  transition: background 0.3s ease;
`;

/* ─── Fun Fact message ─── */

const MessageBox = styled(motion.div)`
  padding: ${DesignTokens.spacing.md};
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: ${DesignTokens.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid #4285F4;
`;

const MessageLabel = styled.span`
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: #4285F4;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 4px;
`;

const MessageText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
  font-weight: ${DesignTokens.typography.fontWeight.medium};
`;

/* ─── Micro-Question ─── */

const QuestionCard = styled(motion.div)`
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: ${DesignTokens.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
`;

const QuestionText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
`;

const QuestionOptions = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.sm};
  justify-content: center;
`;

const QuestionPill = styled.button`
  padding: 8px 16px;
  border-radius: ${DesignTokens.borderRadius.full};
  border: 1px solid ${props => props.$selected ? '#4285F4' : 'rgba(255, 255, 255, 0.15)'};
  background: ${props => props.$selected ? 'rgba(66, 133, 244, 0.25)' : 'rgba(255, 255, 255, 0.06)'};
  color: ${props => props.$selected ? '#8AB4F8' : 'rgba(255, 255, 255, 0.7)'};
  font-size: ${DesignTokens.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    background: rgba(66, 133, 244, 0.15);
    border-color: rgba(66, 133, 244, 0.4);
  }
`;

const GreatChoiceText = styled(motion.p)`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: #22C55E;
  margin: 8px 0 0 0;
  font-weight: ${DesignTokens.typography.fontWeight.medium};
`;

/* ─── Countdown Timer ─── */

const CountdownPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${DesignTokens.spacing.md};
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: ${DesignTokens.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.08);
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.5);
`;

/* ─── Completion Celebration ─── */

const CelebrationOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

const CompletionTitle = styled(motion.h2)`
  font-size: ${DesignTokens.typography.fontSize.h1};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  text-align: center;
  margin: 0 0 ${DesignTokens.spacing.sm} 0;
  line-height: ${DesignTokens.typography.lineHeight.heading};
`;

const CompletionSubtitle = styled(motion.p)`
  font-size: ${DesignTokens.typography.fontSize.body};
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0;
`;

/* ─── Icon helper ─── */

const StepIcon = ({ icon, size = 20 }) => {
  switch (icon) {
    case 'map-pin':
      return <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a5 5 0 00-5 5c0 4.5 5 11 5 11s5-6.5 5-11a5 5 0 00-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z"/></svg>;
    case 'route':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="none"/></svg>;
    case 'calculator':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="8" y2="18"/><line x1="12" y1="18" x2="12" y2="18"/><line x1="16" y1="18" x2="16" y2="18"/><line x1="8" y1="10" x2="16" y2="10"/></svg>;
    default:
      return null;
  }
};

/* ─── Photo Carousel sub-component ─── */

const PhotoCarousel = ({ country }) => {
  const [photos, setPhotos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);

  // Remove broken images at runtime
  const handleImageError = useCallback(() => {
    setPhotos(prev => {
      const next = prev.filter((_, i) => i !== activeIndex);
      if (next.length && activeIndex >= next.length) {
        setActiveIndex(0);
      }
      return next;
    });
  }, [activeIndex]);

  useEffect(() => {
    if (!country) return;
    let cancelled = false;
    imagesApi.searchImages(country + ' travel', 6, 'unsplash')
      .then(res => {
        if (cancelled) return;
        const valid = (res.data || [])
          .filter(img => img.imageUrl && img.mimeType?.startsWith('image/'))
          .slice(0, 4)
          .map(img => ({ url: img.thumbnailUrl || img.imageUrl, alt: img.altText }));
        if (valid.length) setPhotos(valid);
      })
      .catch(() => { /* graceful fallback — render nothing */ });
    return () => { cancelled = true; };
  }, [country]);

  useEffect(() => {
    if (photos.length < 2) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % photos.length);
    }, 4500);
    return () => clearInterval(intervalRef.current);
  }, [photos.length]);

  if (!photos.length) return null;

  return (
    <CarouselWrapper>
      <AnimatePresence mode="wait">
        <CarouselImage
          key={photos[activeIndex]?.url || activeIndex}
          src={photos[activeIndex]?.url}
          alt={photos[activeIndex]?.alt || `${country} travel`}
          onError={handleImageError}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        />
      </AnimatePresence>
      {photos.length > 1 && (
        <CarouselDots>
          {photos.map((_, i) => (
            <CarouselDot key={i} $active={i === activeIndex} />
          ))}
        </CarouselDots>
      )}
    </CarouselWrapper>
  );
};

/* ─── Micro-Question sub-component ─── */

const MicroQuestion = () => {
  const [question] = useState(() => MICRO_QUESTIONS[Math.floor(Math.random() * MICRO_QUESTIONS.length)]);
  const [selected, setSelected] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const handleSelect = useCallback((option) => {
    setSelected(option);
    setTimeout(() => setDismissed(true), 1500);
  }, []);

  if (dismissed) return null;

  return (
    <QuestionCard
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4 }}
    >
      <QuestionText>{question.question}</QuestionText>
      <QuestionOptions>
        <QuestionPill
          $selected={selected === 'A'}
          onClick={() => !selected && handleSelect('A')}
        >
          {question.optionA}
        </QuestionPill>
        <QuestionPill
          $selected={selected === 'B'}
          onClick={() => !selected && handleSelect('B')}
        >
          {question.optionB}
        </QuestionPill>
      </QuestionOptions>
      <AnimatePresence>
        {selected && (
          <GreatChoiceText
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Great choice! ✓
          </GreatChoiceText>
        )}
      </AnimatePresence>
    </QuestionCard>
  );
};

/* ─── Main Component ─── */

const ItineraryGenerationProgress = ({ progress, rotatingMessage, estimatedDuration = { min: 30, max: 60 }, country, isSurprise }) => {
  const [showCelebration, setShowCelebration] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const theme = getDestinationTheme(country);

  const activeStepIndex = progress ? getActiveStepIndex(progress.state) : 0;
  const isComplete = progress?.state === 'COMPLETED';

  // Show micro-question after 15s elapsed
  useEffect(() => {
    if (progress?.elapsedSeconds >= 15 && !showQuestion) {
      setShowQuestion(true);
    }
  }, [progress?.elapsedSeconds, showQuestion]);

  useEffect(() => {
    if (isComplete) {
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isComplete]);

  if (!progress) {
    return (
      <Container>
        <Heading
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Preparing Your Journey
        </Heading>
        <Subheading>Connecting to generation service...</Subheading>
        <ProgressTrack>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: '15%' }}
            transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
          />
        </ProgressTrack>
      </Container>
    );
  }

  if (isComplete) {
    return (
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{ position: 'relative' }}
      >
        {showCelebration && (
          <CelebrationOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ThemedParticles theme={theme} count={14} />
          </CelebrationOverlay>
        )}

        <CompletionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {isSurprise && country
            ? `${theme.emoji} ${country}!`
            : 'Your Journey Awaits'}
        </CompletionTitle>
        <CompletionSubtitle
          initial={isSurprise ? { opacity: 0, filter: 'blur(12px)' } : { opacity: 0 }}
          animate={isSurprise ? { opacity: 1, filter: 'blur(0px)' } : { opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          {isSurprise ? 'Surprise! Taking you there...' : 'Taking you to your itinerary...'}
        </CompletionSubtitle>

        <ProgressTrack style={{ marginTop: '24px' }}>
          <ProgressFill
            initial={{ width: '90%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.5 }}
          />
        </ProgressTrack>
      </Container>
    );
  }

  const progressPercent = progress.progressPercentage != null
    ? progress.progressPercentage
    : activeStepIndex === 0 ? 20
    : activeStepIndex === 1 ? 55
    : 85;

  // Countdown timer
  const elapsed = progress.elapsedSeconds || 0;
  const remaining = Math.max(0, estimatedDuration.max - elapsed);
  const formatCountdown = (secs) => {
    if (secs <= 0) return 'Almost there...';
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return mins > 0 ? `~${mins}m ${s}s remaining` : `~${s}s remaining`;
  };

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ position: 'relative' }}
    >
      {country && (
        <CelebrationOverlay style={{ opacity: 0.5 }}>
          <ThemedParticles theme={theme} count={8} />
        </CelebrationOverlay>
      )}
      <Heading>{isSurprise ? 'Brewing Your Surprise...' : 'Crafting Your Perfect Journey'}</Heading>
      <Subheading>Sit back while we plan something amazing</Subheading>

      <StepsRow>
        {STEPS.map((step, i) => {
          const status = i < activeStepIndex ? 'completed'
            : i === activeStepIndex ? 'active'
            : 'pending';

          return (
            <React.Fragment key={step.id}>
              {i > 0 && (
                <StepConnector $completed={i <= activeStepIndex} />
              )}
              <StepItem>
                <StepCircle
                  $status={status}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: i * 0.15 }}
                  style={status === 'active' && theme.colors ? {
                    background: `linear-gradient(135deg, ${theme.colors[0]}, ${theme.colors[1]})`,
                    boxShadow: `0 0 20px ${theme.colors[0]}80`,
                  } : undefined}
                >
                  {status === 'completed' ? (
                    <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7.5L5.5 11L12 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <StepIcon icon={step.icon} />
                  )}
                </StepCircle>
                <StepLabel $active={status !== 'pending'}>
                  {step.label}
                </StepLabel>
              </StepItem>
            </React.Fragment>
          );
        })}
      </StepsRow>

      <ProgressTrack>
        <ProgressFill
          initial={{ width: 0 }}
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={theme.colors ? {
            background: `linear-gradient(90deg, ${theme.colors[0]}, ${theme.colors[1]})`,
          } : undefined}
        />
      </ProgressTrack>

      <ProgressLabel>
        {progressPercent}% complete
      </ProgressLabel>

      {/* Photo Carousel */}
      {country && <PhotoCarousel country={country} />}

      {/* Fun Fact Message */}
      <AnimatePresence mode="wait">
        {rotatingMessage && (
          <MessageBox
            key={rotatingMessage}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 15 }}
            transition={{ duration: 0.4 }}
          >
            <MessageLabel>{theme.emoji} Did you know?</MessageLabel>
            <MessageText>{rotatingMessage}</MessageText>
          </MessageBox>
        )}
      </AnimatePresence>

      {/* Micro-Question (appears at ~15s) */}
      <AnimatePresence>
        {showQuestion && (
          <MicroQuestion key="micro-q" />
        )}
      </AnimatePresence>

      {/* Countdown Timer */}
      {elapsed > 0 && (
        <CountdownPill>
          {formatCountdown(remaining)}
        </CountdownPill>
      )}
    </Container>
  );
};

export default ItineraryGenerationProgress;
